import { useState, useCallback } from 'react';
import _camelCase from 'lodash/camelCase';
import _get from 'lodash/get';
import { useIntl } from 'react-intl';

const GRAPHQL_URL = __GRAPHQL_URL__; // eslint-disable-line no-undef

/**
 * Enhanced GraphQL mutation hook with proper error handling and result propagation
 * @param {Object} options - Hook configuration options
 * @param {string} options.query - GraphQL mutation query string
 * @param {string} options.token - Authentication token
 * @param {number} [options.timeoutMs=15000] - Request timeout in milliseconds
 * @returns {Object} - Mutation state and execution function
 */
function useMutation2({
  query,
  token,
  timeoutMs = 15000,
}) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);
  const [data, setData] = useState(null);
  const { formatMessage: t } = useIntl();

  /**
   * Execute the GraphQL mutation
   * @param {Object} variables - GraphQL variables for the mutation
   * @returns {Promise<Object>} - The mutation result data
   * @throws {Error} - If the mutation fails or times out
   */
  const exec = useCallback(async (variables) => {
    const controller = new AbortController();
    const { signal } = controller;

    setLoading(true);
    setErrors(null);
    setData(null);

    // Create abort timeout
    const timeoutId = setTimeout(() => {
      controller.abort();
      const timeoutError = new Error(`Request timed out after ${timeoutMs / 1000} seconds`);
      setErrors({ network: timeoutError });
      setLoading(false);
      throw timeoutError;
    }, timeoutMs);

    try {
      // Make the GraphQL request
      const response = await fetch(GRAPHQL_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/vnd.app.v4+json',
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          variables,
        }),
        signal,
      });

      // Clear the timeout since we got a response
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorMessage = `Network request failed with status ${response.status}: ${response.statusText}`;
        const networkError = new Error(errorMessage);
        setErrors({ network: networkError });
        setLoading(false);
        throw networkError;
      }

      // Parse the response
      const result = await response.json();

      // Check for GraphQL errors
      if (result.errors && result.errors.length > 0) {
        const newErrors = {};
        result.errors.forEach((error) => {
          const specificErrors = _get(error, 'extensions.path', null) || _get(error, 'path', null);
          if (specificErrors) {
            specificErrors.forEach((specificError) => {
              const translationKey = `formError.${specificError}.${_camelCase(error.message)}`;
              const message = t({ id: translationKey });
              newErrors[specificError] = message;
            });
            return;
          }

          if (error && error.message) {
            const translationKey = `error.${_camelCase(error.message)}`;
            const message = t({ id: translationKey });
            newErrors.global = message;
          } else {
            newErrors.global = true;
          }
        });
        setErrors(newErrors);
        setLoading(false);
        const error = new Error(newErrors.global || 'GraphQL mutation failed');
        error.graphqlErrors = newErrors;
        throw error.graphqlErrors;
      }

      // Set successful data
      setData(result.data);
      setLoading(false);
      return result.data;
    } catch (e) {
      // Don't handle AbortError from our own timeout - it's already handled above
      if (e.name !== 'AbortError') {
        clearTimeout(timeoutId);
        setErrors({ network: e });
        // eslint-disable-next-line no-console
        console.error('GraphQL mutation error:', e);
      }
      setLoading(false);
      throw e;
    }
  }, [query, token, timeoutMs]);

  /**
   * Reset the hook state
   */
  const reset = useCallback(() => {
    setLoading(false);
    setErrors(null);
    setData(null);
  }, []);

  return {
    exec,
    reset,
    loading,
    errors,
    data,
  };
}

export default useMutation2;



// WEBPACK FOOTER //
// ./hooks/useMutation2.js